import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiAlertCircle, FiArrowRight, FiRefreshCw, FiShield, FiTrendingUp, FiActivity } = FiIcons;

const questions = [
  {
    id: 1,
    text: "How would you describe your knowledge of investments?",
    options: [
      { text: "Very Limited - I mainly trust bank FDs & PPF", score: 1 },
      { text: "Basic - I invest in mutual funds but worry about losses", score: 3 },
      { text: "Advanced - I understand stocks, volatility & long-term compounding", score: 5 },
    ],
  },
  {
    id: 2,
    text: "If your portfolio drops by 20% in a month due to a market crash, what would you do?",
    options: [
      { text: "Panic and withdraw immediately to prevent further loss", score: 1 },
      { text: "Wait patiently for recovery (Hold)", score: 3 },
      { text: "Invest more to buy at lower prices (Opportunity)", score: 5 },
    ],
  },
  {
    id: 3,
    text: "What is your primary expectation from your investments?",
    options: [
      { text: "Capital Protection - I cannot afford to lose any money", score: 1 },
      { text: "Balanced Growth - Beat inflation with moderate safety", score: 3 },
      { text: "Aggressive Growth - Maximize wealth, even if risky", score: 5 },
    ],
  },
  {
    id: 4,
    text: "How stable is your current income source?",
    options: [
      { text: "Variable / Unstable (Freelance, Business in early stage)", score: 1 },
      { text: "Moderately Stable (Private job)", score: 3 },
      { text: "Very Secure (Govt job, Established Business, High savings)", score: 5 },
    ],
  },
  {
    id: 5,
    text: "How long can you keep your money invested without needing it?",
    options: [
      { text: "Less than 3 years", score: 1 },
      { text: "3 to 7 years", score: 3 },
      { text: "More than 7 years", score: 5 },
    ],
  }
];

const RiskProfiler = ({ onComplete, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const handleOptionSelect = (score) => {
    const newScore = totalScore + score;
    if (currentStep < questions.length - 1) {
      setTotalScore(newScore);
      setCurrentStep(currentStep + 1);
    } else {
      setTotalScore(newScore);
      setShowResult(true);
    }
  };

  const getProfileResult = () => {
    // Max Score: 25, Min Score: 5
    // Low: 5-11
    // Medium: 12-19
    // High: 20-25
    if (totalScore <= 11) return { 
        label: 'Low', 
        title: 'Conservative Investor',
        desc: 'You prioritize safety over returns. You prefer guaranteed income products or debt funds and get anxious about market volatility.',
        icon: FiShield,
        color: 'text-green-600',
        bg: 'bg-green-50'
    };
    if (totalScore <= 19) return { 
        label: 'Medium', 
        title: 'Moderate Investor',
        desc: 'You want a balance between growth and safety. You can handle some short-term fluctuations to beat inflation in the long run.',
        icon: FiActivity,
        color: 'text-yellow-600',
        bg: 'bg-yellow-50'
    };
    return { 
        label: 'High', 
        title: 'Aggressive Investor',
        desc: 'You are a wealth creator. You understand that volatility is the price of high returns and are willing to take risks for long-term gains.',
        icon: FiTrendingUp,
        color: 'text-red-600',
        bg: 'bg-red-50'
    };
  };

  const result = showResult ? getProfileResult() : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
      >
        {!showResult ? (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Risk Profile Assessment</h3>
              <span className="text-sm font-medium text-indigo-600">
                Question {currentStep + 1}/{questions.length}
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
              ></div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h4 className="text-lg font-medium text-gray-800 mb-6">
                  {questions[currentStep].text}
                </h4>
                <div className="space-y-3">
                  {questions[currentStep].options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(option.score)}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all flex items-center justify-between group"
                    >
                      <span className="text-gray-700 group-hover:text-indigo-700">{option.text}</span>
                      <SafeIcon icon={FiArrowRight} className="text-gray-300 group-hover:text-indigo-500" />
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${result.bg}`}>
              <SafeIcon icon={result.icon} className={`text-3xl ${result.color}`} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{result.title}</h3>
            <div className="inline-block px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-semibold mb-6">
              Risk Profile: {result.label}
            </div>
            <p className="text-gray-600 mb-8 leading-relaxed">
              {result.desc}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => onComplete(result.label)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiCheck} />
                <span>Apply This Profile</span>
              </button>
              <button
                onClick={() => {
                  setTotalScore(0);
                  setCurrentStep(0);
                  setShowResult(false);
                }}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiRefreshCw} />
                <span>Retake Quiz</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Close Button (Top Right) */}
        {!showResult && (
           <button 
             onClick={onClose}
             className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
           >
             ✕
           </button>
        )}
      </motion.div>
    </div>
  );
};

export default RiskProfiler;